<script lang="ts">
	import {
		VisXYContainer,
		VisLine,
		VisAxis,
		VisCrosshair,
		VisTooltip,
		VisBulletLegend,
	} from '@unovis/svelte'
	import { differenceInDays } from 'date-fns'
	import LoaderCircle from 'lucide-svelte/icons/loader-circle'

	import { CHAINS_CONFIG } from '$lib/constants/chainsConfig'
	import { TIME_TWO_DIGIT_FORMATTER } from '$lib/constants/formatters'
	import type { ChainId, ChartRange, LineChartRecord } from '$lib/models'
	import * as m from '$lib/paraglide/messages'
	import { prettifyTps } from '$lib/utils/prettifyMetrics'
	import { cn } from '$lib/utils/shadcn'
	import ChainspectLogo from '~icons/brand/logo'

	export let chainIds: ChainId[]
	export let chartData: LineChartRecord[]
	export let chartRange: ChartRange
	export let isLoading: boolean

	const COLORS = ['#027dfd', '#1be6ab']

	const tickFormatter = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: 'UTC',
	})

	const dateFormatter = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	})

	const yTickFormat = (value: number) => prettifyTps(value, 'none')

	const xTickFormat = (tick: number) => {
		const record = chartData[tick]

		if (!record || !record.x) {
			return ''
		}

		if (!record.x1) {
			return (chartRange === 'day' ? TIME_TWO_DIGIT_FORMATTER : dateFormatter).format(record.x)
		}

		if (chartRange !== 'day' && differenceInDays(record.x, record.x1) === 0) {
			return dateFormatter.format(record.x)
		}

		return ''
	}

	const template = (d: LineChartRecord) => {
		if (d.x && d.x1 && d.y && d.y1 && chainIds.length === 2) {
			const firstDate = tickFormatter.format(d.x)
			const secondDate = tickFormatter.format(d.x1)

			const firstTps = prettifyTps(d.y)
			const secondTps = prettifyTps(d.y1)

			const firstChain = CHAINS_CONFIG[chainIds[0]].name
			const secondChain = CHAINS_CONFIG[chainIds[1]].name

			if (d.y >= d.y1) {
				return m.chart_tooltip_multiple({
					upperDate: firstDate,
					lowerDate: secondDate,
					upperTps: firstTps,
					lowerTps: secondTps,
					upperChain: firstChain,
					lowerChain: secondChain,
					upperColor: COLORS[0],
					lowerColor: COLORS[1],
				})
			}

			return m.chart_tooltip_multiple({
				upperDate: secondDate,
				lowerDate: firstDate,
				upperTps: secondTps,
				lowerTps: firstTps,
				upperChain: secondChain,
				lowerChain: firstChain,
				upperColor: COLORS[1],
				lowerColor: COLORS[0],
			})
		}

		if (d.x && d.y && !d.y1 && chainIds.length === 2) {
			const date = tickFormatter.format(d.x)
			const tps = prettifyTps(d.y)
			const chain = CHAINS_CONFIG[chainIds[0]].name

			return m.chart_tooltip_single({ date, tps, chain, color: COLORS[0] })
		}

		if (d.x1 && d.y1 && !d.y && chainIds.length === 2) {
			const date = tickFormatter.format(d.x1)
			const tps = prettifyTps(d.y1)
			const chain = CHAINS_CONFIG[chainIds[1]].name

			return m.chart_tooltip_single({ date, tps, chain, color: COLORS[1] })
		}

		const date = tickFormatter.format(d.x)
		const tps = prettifyTps(d.y)
		const chain = CHAINS_CONFIG[chainIds[0]]?.name

		if (chain != null) {
			return m.chart_tooltip({ date, tps, chain })
		}

		return m.chart_tooltip_without_chain({ date, tps })
	}

	const x = (_: LineChartRecord, i: number) => i
	const y = [(d: LineChartRecord) => d.y, (d: LineChartRecord) => d?.y1]

	const color = (_: LineChartRecord, i: number) => COLORS[i]

	$: yDomain =
		Array.isArray(chartData) && chartData.length > 0
			? <[number, number]>[
					0,
					Math.max(
						...chartData.map(dataPoint => {
							if (dataPoint.y != null && dataPoint.y1 != null) {
								return Math.max(dataPoint.y, dataPoint.y1)
							}

							return dataPoint.y
						})
					) * 1.1,
				]
			: undefined
</script>

<div class={cn('chart-container relative', chainIds.length === 2 ? 'h-[330px]' : 'h-[300px]')}>
	{#if isLoading}
		<div class="bg-background absolute inset-0 z-1 flex flex-col items-center justify-center">
			<LoaderCircle class="animate-spin" size="24" />
			<p class="mt-2 font-semibold">{m.chart_loading()}</p>
		</div>
	{/if}

	<VisXYContainer duration={1} height="300" scaleByDomain {yDomain}>
		<VisLine {color} curveType="linear" data={chartData} {x} {y} />
		<VisAxis gridLine={false} tickFormat={xTickFormat} type="x" />
		<VisAxis tickFormat={yTickFormat} type="y" />
		<VisCrosshair {color} data={chartData} {template} {x} {y} />
		<VisTooltip
			attributes={{
				'data-type': 'chart-tooltip',
			}}
			verticalShift={5}
		/>
	</VisXYContainer>

	{#if chainIds.length === 2}
		<div class="mt-1 flex items-center justify-center">
			<VisBulletLegend
				items={chainIds.map((id, i) => ({
					name: CHAINS_CONFIG[id].name,
					color: COLORS[i],
				}))}
			/>
		</div>
	{/if}

	<ChainspectLogo
		class="text-muted-foreground pointer-events-none absolute top-1/2 left-1/2 w-5/6 -translate-x-1/2 -translate-y-1/2 pl-6 opacity-10 select-none sm:pl-0"
	/>
</div>
